/**
 * home_pref.repository.ts — 로그인 직후 첫 화면 설정을 사용자별로 Firestore 보관(2026-11-02).
 *
 *   문서: users/{uid}/preferences/home = { kind, tenant?, biz?, updated_at } (domain/home_pref.ts)
 *   firestore.rules 의 `match /users/{uid}/preferences/{key}` 가 본인 read/write 를 이미 허용 — rules 변경·배포 불필요.
 *   dashboardPrefsRepo 구조를 복제. 로그아웃 상태면 watch 는 즉시 기본값, save 는 no-op.
 */
import { deleteField, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { DEFAULT_HOME_PREF, parseHomePref, type HomePref } from "@/domain/home_pref";

const DOC_ID = "home";

export const homePrefRepo = {
  /** 본인 첫 화면 설정 구독. 미로그인·문서 없음·읽기 실패 → 기본(내 업무). */
  watch(uid: string | null, cb: (pref: HomePref) => void): () => void {
    if (!uid) {
      cb(DEFAULT_HOME_PREF);
      return () => {};
    }
    return onSnapshot(
      doc(db, "users", uid, "preferences", DOC_ID),
      (snap) => cb(parseHomePref(snap.data())),
      () => cb(DEFAULT_HOME_PREF),
    );
  },

  /** 저장(merge). 법인 홈이 아니면 tenant·biz 필드를 지워 옛 값이 남지 않게 한다. */
  async save(pref: HomePref): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    await setDoc(
      doc(db, "users", uid, "preferences", DOC_ID),
      pref.kind === "tenant"
        ? { kind: pref.kind, tenant: pref.tenant, biz: pref.biz, updated_at: serverTimestamp() }
        : { kind: pref.kind, tenant: deleteField(), biz: deleteField(), updated_at: serverTimestamp() },
      { merge: true },
    );
  },
};
